<template>
  <div class="candidate-application-list">
    <div v-if="rows.length" class="candidate-application-list__items">
      <article
        v-for="item in rows"
        :key="item.id"
        class="candidate-application-list__row"
        role="button"
        tabindex="0"
        :aria-label="`${item.name_vacancy} — ${item.name_companies}`"
        @click="$emit('open-recruitment', item.id_recruitment_requirements)"
        @keydown.enter="$emit('open-recruitment', item.id_recruitment_requirements)"
        @keydown.space.prevent="$emit('open-recruitment', item.id_recruitment_requirements)"
      >
        <div class="candidate-application-list__main">
          <div class="candidate-application-list__company">
            <v-icon size="14">mdi-office-building-outline</v-icon>
            <span>{{ item.name_companies || "—" }}</span>
          </div>
          <div class="candidate-application-list__position">{{ item.name_vacancy || "—" }}</div>
        </div>

        <div class="candidate-application-list__status">
          <span class="candidate-application-list__label">{{ $t("recruitment.apps.status") }}</span>
          <v-chip size="x-small" color="primary">
            {{ statusLabel(item.status) }}
          </v-chip>
        </div>

        <div class="candidate-application-list__submission">
          <span class="candidate-application-list__label">{{ $t("recruitment.apps.submission") }}</span>
          <RecruitmentSubmissionStatusButton
            v-if="item.requirement_type === 'client'"
            :application="item"
            @open="$emit('open-submission', item)"
          />
          <span v-else class="candidate-application-list__empty">—</span>
        </div>

        <div class="candidate-application-list__actions" :aria-label="$t('recruitment.apps.actions')">
          <v-btn
            icon="mdi-pencil-outline"
            size="small"
            variant="text"
            :aria-label="$t('app.actions.edit')"
            @click.stop="$emit('edit', item)"
          />
          <v-btn
            icon="mdi-delete-outline"
            size="small"
            variant="text"
            color="error"
            :aria-label="$t('app.actions.delete')"
            @click.stop="$emit('delete', item.id)"
          />
          <v-icon size="18" class="candidate-application-list__chevron">mdi-chevron-right</v-icon>
        </div>
      </article>
    </div>
    <v-alert v-else type="info" density="compact">
      {{ $t("recruitment.apps.empty") }}
    </v-alert>
  </div>
</template>

<script>
import RecruitmentSubmissionStatusButton from "@/components/recruitment/RecruitmentSubmissionStatusButton.vue";

export default {
  name: "CandidateApplicationList",
  components: { RecruitmentSubmissionStatusButton },
  props: {
    rows: { type: Array, default: () => [] },
  },
  emits: ["open-recruitment", "open-submission", "edit", "delete"],
  methods: {
    statusLabel(status) {
      const key = `recruitment.status.${status}`;
      const translated = this.$t(key);
      return translated === key ? status || "—" : translated;
    },
  },
};
</script>

<style scoped>
.candidate-application-list__items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.candidate-application-list__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 10px 8px 10px 12px;
  border: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.1));
  border-radius: var(--app-radius-base);
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.candidate-application-list__row:hover,
.candidate-application-list__row:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.35);
  box-shadow: var(--app-shadow-sm);
  outline: none;
  transform: translateY(-1px);
}

.candidate-application-list__main,
.candidate-application-list__status,
.candidate-application-list__submission {
  min-width: 0;
}

.candidate-application-list__company,
.candidate-application-list__position {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.candidate-application-list__company {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--app-text-default);
  font-size: 0.72rem;
}

.candidate-application-list__position {
  margin-top: 2px;
  color: var(--app-text-strong);
  font-size: 0.86rem;
  font-weight: 600;
}

.candidate-application-list__label {
  display: block;
  margin-bottom: 3px;
  color: var(--app-text-default);
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.candidate-application-list__submission {
  min-width: 72px;
}

.candidate-application-list__empty {
  color: var(--app-text-default);
  opacity: 0.55;
}

.candidate-application-list__actions {
  display: flex;
  align-items: center;
  gap: 0;
  padding-left: 4px;
  border-left: 1px solid var(--ncrm-color-border-subtle, rgba(var(--v-theme-on-surface), 0.1));
}

.candidate-application-list__chevron {
  color: var(--app-text-default);
  opacity: 0.45;
}

@media (max-width: 700px) {
  .candidate-application-list__row {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }

  .candidate-application-list__status,
  .candidate-application-list__submission {
    grid-column: auto;
  }

  .candidate-application-list__status {
    grid-row: 2;
  }

  .candidate-application-list__submission {
    grid-row: 2;
  }

  .candidate-application-list__actions {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
}
</style>
